import { useOktaAuth } from "@okta/okta-react";
import { useMemo } from "react";
import {
  parseGroups,
  type AireAuthContext,
  type OktaJwtClaims,
} from "@lio-aire/auth-utils/browser";
import { loadShellEnv } from "../env.js";

/**
 * Derives the `AireAuthContext` from the in-memory Okta ID-token claims, and exposes the raw
 * ACCESS token so a module's API client can present it as `Authorization: Bearer` to the shared
 * API's Lambda authorizer.
 *
 * Token split — the security-critical distinction (auth.md §5.3):
 *  - The **ID token** describes the user *to this browser app*. Its `aud` is the Okta client ID.
 *    It is the correct source for the claims that drive UI gating, and that is all it is used for.
 *  - The **access token** is the credential *for the API*. Its `aud` is the API resource audience
 *    `api://lio-aire`, which is what `OKTA_AUDIENCE` on the authorizer validates against.
 *
 * Sending the ID token to the API is the anti-pattern that caused #120 and its UAT recurrence: the
 * authorizer rejected every call on the `aud` check, which API Gateway surfaces as a bare 403
 * `Forbidden`. This hook therefore exposes ONLY the access token — the ID token is deliberately not
 * part of the public result, so a module cannot reintroduce the bug by reaching for the wrong one.
 *
 * The permission model (apps × roles × entities, plus admin/auditor flags) is owned by
 * `@lio-aire/auth-utils`; this hook reuses its `parseGroups` and types rather than re-declaring them
 * (auth.md §6.3). The browser subpath of auth-utils carries no server/Node dependency. Tokens are
 * read from Okta's in-memory token manager via `authState`, never from storage
 * (frontend-security.md §5.1).
 *
 * This is the single platform auth hook — the former per-app copies (close exposed only
 * `authContext`; admin additionally exposed `idToken`) are unified here into the superset.
 */

export interface AuthContextResult {
  /** True once Okta has resolved the initial auth state. */
  readonly isResolved: boolean;
  readonly isAuthenticated: boolean;
  /** Null until signed in with a parseable ID token. */
  readonly authContext: AireAuthContext | null;
  /**
   * The raw in-memory ACCESS token, or undefined when signed out. This is the only credential any
   * `/api/*` call may present as `Authorization: Bearer`; see the token split in the module doc.
   */
  readonly accessToken: string | undefined;
}

/** Synthetic identity shared by the browser half of `pnpm dev:planning`. The matching server
 * claims live in planning-api/dev-server.ts; both values are deliberately non-production fixture
 * identifiers. The local identity is an AIRE administrator so the VAULT administration workflow
 * can be exercised without weakening any deployed environment. `loadShellEnv` is the security gate
 * that makes this reachable only in local Vite development mode. */
export const LOCAL_PLANNING_AUTH_CONTEXT: AireAuthContext = {
  sub: "fixture|planning-local-finance-developer",
  email: "planning-local@example.invalid",
  permissions: [
    { app: "planning", role: "preparer", entity: "ALL" },
    { app: "planning", role: "approver", entity: "ALL" },
  ],
  isAdmin: true,
  isAuditor: false,
};

interface OktaIdTokenClaims {
  readonly sub?: string;
  readonly email?: string;
  readonly groups?: unknown;
}

export function deriveAuthContext(claims: OktaIdTokenClaims | undefined): AireAuthContext | null {
  if (claims === undefined || typeof claims.sub !== "string" || claims.sub === "") {
    return null;
  }

  const groups = Array.isArray(claims.groups)
    ? claims.groups.filter((group): group is string => typeof group === "string")
    : [];

  const normalized: OktaJwtClaims = {
    sub: claims.sub,
    ...(typeof claims.email === "string" ? { email: claims.email } : {}),
    groups,
  };

  const { permissions, isAdmin, isAuditor } = parseGroups(groups);
  return {
    sub: normalized.sub,
    email: normalized.email ?? "",
    permissions,
    isAdmin,
    isAuditor,
  };
}

export function useAuthContext(): AuthContextResult {
  const { authState } = useOktaAuth();
  const { localAuthEnabled } = loadShellEnv();

  return useMemo<AuthContextResult>(() => {
    if (localAuthEnabled) {
      return {
        isResolved: true,
        isAuthenticated: true,
        authContext: LOCAL_PLANNING_AUTH_CONTEXT,
        // The local API adapter never validates this sentinel as a JWT. It builds the same
        // synthetic claims server-side, so no development token can become production authority.
        accessToken: "aire-local-development-token",
      };
    }
    if (authState === null || authState === undefined) {
      return {
        isResolved: false,
        isAuthenticated: false,
        authContext: null,
        accessToken: undefined,
      };
    }

    // UI gating reads ID-token claims; the API credential is the access token. Both come from the
    // same in-memory token manager, never from browser storage.
    const claims = authState.idToken?.claims as OktaIdTokenClaims | undefined;
    const isAuthenticated = authState.isAuthenticated === true;
    return {
      isResolved: true,
      isAuthenticated,
      authContext: isAuthenticated ? deriveAuthContext(claims) : null,
      accessToken: isAuthenticated ? authState.accessToken?.accessToken : undefined,
    };
  }, [authState, localAuthEnabled]);
}
